import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  ReturnSystemView,
  StaticSystemViews,
} from "@redotech/redo-return-operations-common/views/default-return-views";
import {
  NEEDS_ACTION,
  needsActionEntryFilters,
} from "@redotech/redo-return-operations-common/views/needs-action";
import { ViewTypesNames } from "@redotech/redo-views-common/views";
import ReturnsIcon from "@redotech/redo-web/icon-old/flip-backward.svg";
import { recordViewHref } from "../../record-links/record-links";
import { createReturnsListAdvancedFilters } from "../../returns-filters";
import { getReturnListSystemViewLabel } from "../../returns-list-labels";
import type { HomeT, SearchFilter } from "./record-home-source";
import { CardCopy, returnRecordSource } from "./return-record-source";

/** Copy for every returns catalog entry, in the prototype's words. */
const RETURN_CARD_COPY: Record<
  (typeof NEEDS_ACTION.returns)[number]["id"],
  CardCopy
> = {
  [ReturnSystemView.STUCK_IN_TRANSIT]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} return shipments haven't moved in over 10 days", {
        count,
      }),
      body: t(
        "These packages are still marked in transit, but the carrier hasn't provided a new tracking update in more than 10 days. They may be delayed or lost. Review the shipments and either wait for an update, file a carrier claim, or close the return if you're handling it another way.",
      ),
    }),
  },
  [ReturnSystemView.AWAITING_PROCESSING]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t(
        "{{count}} delivered returns are still waiting to be processed",
        { count },
      ),
      body: t(
        "These packages were delivered back to you more than 10 days ago, but the returns still haven't been processed. Customers may still be waiting for their refund, store credit, or exchange. Process them all at once instead of opening each return.",
      ),
    }),
  },
  [ReturnSystemView.PARTIALLY_PROCESSED]: {
    tone: "warning",
    processText: (t) => t("Process remaining items"),
    copy: (t, count) => ({
      title: t("{{count}} returns still have items waiting to be processed", {
        count,
      }),
      body: t(
        "Some items in these returns were already processed, but one or more items were left unfinished. Finish processing the remaining items so the returns can be completed and customers receive everything they're owed.",
      ),
    }),
  },
  [ReturnSystemView.AWAITING_REMIT]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t(
        "{{count}} warehouse-processed returns are waiting for payment",
        { count },
      ),
      body: t(
        "Your warehouse has received and verified these returns, but the refund, store credit, or exchange hasn't been issued to the customer yet. Finish processing them to send what the customers are owed.",
      ),
    }),
  },
  [ReturnSystemView.FAILED_RMA_CREATION]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} returns couldn't create an RMA", { count }),
      body: t(
        "We weren't able to create the RMA for these returns in your connected system. This can happen when the integration rejects the request or required information is missing. Review the errors and retry RMA creation after resolving the issue.",
      ),
    }),
  },
  [ReturnSystemView.LABEL_CREATION_FAILED]: {
    tone: "error",
    copy: (t, count) => ({
      title: t("{{count}} returns couldn't generate a shipping label", {
        count,
      }),
      body: t(
        "We couldn't create return labels for these customers. This is commonly caused by an invalid address, unavailable shipping rate, or carrier configuration issue. Review each return, fix the issue, and create a new label.",
      ),
    }),
  },
  [ReturnSystemView.REFUND_PAYMENT_FAILED]: {
    tone: "error",
    copy: (t, count) => ({
      title: t("{{count}} customer refunds failed to send", { count }),
      body: t(
        "These returns were processed, but Shopify reported that the refund payment failed. The customers may not have received their money. Reissue the refunds in Shopify, then mark the issue as resolved here.",
      ),
    }),
  },
  [ReturnSystemView.RESTOCK_FAILED]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} returns have items that couldn't be restocked", {
        count,
      }),
      body: t(
        "We processed these returns, but Shopify couldn't update inventory for one or more returned items. Check the affected SKUs, update inventory in Shopify if needed, then mark them as restocked.",
      ),
    }),
  },
  [ReturnSystemView.OUT_OF_STOCK]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t(
        "{{count}} exchanges can't be fulfilled because items sold out",
        { count },
      ),
      body: t(
        "The replacement items selected by these customers went out of stock before their returns were processed. Choose a different variant, refund the item, or issue store credit so processing can continue.",
      ),
    }),
  },
  [ReturnSystemView.TRACKING_EXCEPTION]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} return shipments have a delivery problem", { count }),
      body: t(
        "These shipments have a carrier exception, such as being returned to the sender or encountering a shipping error. Review the latest tracking information and decide whether to have the customer reship the item, issue their refund, or wait for another carrier update.",
      ),
    }),
  },
  [ReturnSystemView.DELAYED_IN_CUSTOMS]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t("{{count}} international returns are delayed in customs", {
        count,
      }),
      body: t(
        "These shipments are being held by customs. Some may require additional customs information or tax IDs before they can continue. Review the affected shipments and provide any missing information.",
      ),
    }),
  },
  [ReturnSystemView.MISSING_CUSTOMS_INFO]: {
    tone: "warning",
    copy: (t, count) => ({
      title: t(
        "{{count}} international returns are missing customs information",
        { count },
      ),
      body: t(
        "These returns can't clear customs until the missing customs details are added. Review each return and fill in the customs information so the shipments can continue.",
      ),
    }),
  },
};

/** Needs-action reasons as suggestions; they aren't table filters, so the factory doesn't know them. */
function returnNeedsActionFilters(t: HomeT): SearchFilter[] {
  return NEEDS_ACTION.returns.flatMap((entry) => {
    const filters = needsActionEntryFilters(ReturnTypeEnum.RETURN, entry);
    return filters
      ? [
          {
            id: `needs-action:${entry.id}`,
            facet: t("Needs action"),
            value: getReturnListSystemViewLabel(entry.id),
            badgeColor: "error" as const,
            filters,
          },
        ]
      : [];
  });
}

const FLAGGED_FILTERS = StaticSystemViews[ReturnSystemView.FLAGGED].filters;

export const RETURNS_SOURCE = returnRecordSource({
  type: "returns",
  recordType: ReturnTypeEnum.RETURN,
  Icon: ReturnsIcon,
  availability: {
    capabilities: ["returns"],
    permissions: [ReturnsAndClaimsPermissions.VIEW_RETURN],
  },
  viewType: ViewTypesNames.RETURNS,
  countSavedViews: true,
  category: "returns",
  tableFilters: createReturnsListAdvancedFilters,
  extraFilters: returnNeedsActionFilters,
  pinNeedsAction: true,
  quickViews: [
    {
      id: "flagged",
      label: (t) => t("Flagged"),
      filters: FLAGGED_FILTERS,
      href: (routes) =>
        recordViewHref(
          routes.returnsBaseUrl,
          ReturnTypeEnum.RETURN,
          ReturnSystemView.FLAGGED,
        ),
      urgent: false,
    },
  ],
  entries: NEEDS_ACTION.returns,
  cardCopy: RETURN_CARD_COPY,
  copy: (t) => ({
    recordsLabel: t("Returns"),
    allLabel: t("All returns"),
    searchAllLabel: (query) =>
      t("Search all returns for “{{query}}”", { query }),
    recordCount: (count) => t("{{count}} returns", { count }),
    viewRecords: (count) => t("View {{count}} returns", { count }),
    viewLabel: t("View returns"),
    processAll: (count) => t("Process all {{count}} returns", { count }),
    resolvePrompt: (count, view) =>
      `Resolve the ${count} returns flagged “${view}”`,
  }),
  create: ReturnTypeEnum.RETURN,
});
